import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaQuoteRight,
  FaQuoteLeft,
  FaUser,
  FaBuilding,
  FaGlobe,
  FaLanguage,
  FaExclamationTriangle,
  FaPenAlt,
  FaMapMarkerAlt
} from 'react-icons/fa';

const HTMLLesson15 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Short inline quotation -->
<p>As Steve Jobs said: <q>Innovation distinguishes between a leader and a follower.</q></p>

<!-- Multiple short quotes -->
<p>Einstein's famous equation: <q>E = mc²</q> revolutionized physics.</p>
<p>She whispered: <q>I'll be there at 8 PM sharp.</q></p>

<!-- With cite attribute -->
<p>The document states: <q cite="https://example.com/doc">All members must comply.</q></p>`;

  const code2 = `<!-- Block quotation with citation -->
<blockquote cite="https://www.codeflarelimited.com/about">
  <p>Whether you're newly entering the workforce or you're an experienced personnel, 
  you can take advantage of ICT and software development training to gain skills that 
  will help you succeed in an ever-growing world economy and provide both stability 
  and more job opportunities.</p>
  <footer>— CodeFlare Limited Mission Statement</footer>
</blockquote>

<!-- Academic citation -->
<blockquote cite="https://www.goodreads.com/quotes">
  <p>The only way to do great work is to love what you do.</p>
  <footer>— Steve Jobs, <cite>Stanford Commencement Speech</cite></footer>
</blockquote>

<!-- Nested blockquote -->
<blockquote>
  <p>This is a primary quote that spans multiple lines and provides 
  significant content that deserves to be highlighted separately 
  from the main text flow.</p>
  <blockquote>
    <p>This is a nested quote within the main blockquote, 
    showing hierarchical quotation structure.</p>
  </blockquote>
</blockquote>`;

  const code3 = `<!-- Abbreviations with titles -->
<p>The <abbr title="World Health Organization">WHO</abbr> was founded in 1948.</p>
<p><abbr title="HyperText Markup Language">HTML</abbr> is the standard markup language.</p>
<p>We use <abbr title="Cascading Style Sheets">CSS</abbr> for styling web pages.</p>

<!-- Common technical abbreviations -->
<p>
  <abbr title="JavaScript">JS</abbr>,
  <abbr title="Application Programming Interface">API</abbr>,
  <abbr title="Representational State Transfer">REST</abbr>,
  <abbr title="Content Management System">CMS</abbr>
</p>

<!-- Academic and professional abbreviations -->
<p>
  Dr. Smith has a <abbr title="Doctor of Philosophy">PhD</abbr> in 
  <abbr title="Computer Science">CS</abbr> and works at 
  <abbr title="Massachusetts Institute of Technology">MIT</abbr>.
</p>`;

  const code4 = `<!-- Contact information for author -->
<address>
  Written by <a href="mailto:john.doe@example.com">John Doe</a>.<br>
  Visit us at:<br>
  CodeFlare Limited<br>
  123 Tech Park, Innovation Road<br>
  Silicon Valley, CA 94025<br>
  USA
</address>

<!-- Article author contact -->
<address>
  Article author: Jane Smith<br>
  Email: <a href="mailto:jane.smith@example.com">jane.smith@example.com</a><br>
  Phone: <a href="tel:+15551234567">+1 (555) 123-4567</a><br>
  Office: Building C, Suite 405
</address>

<!-- Multiple authors -->
<address>
  Contributors to this document:<br>
  - Sarah Johnson: <a href="mailto:sarah@example.com">sarah@example.com</a><br>
  - Mike Chen: <a href="mailto:mike@example.com">mike@example.com</a><br>
  - Technical Support: <a href="mailto:support@example.com">support@example.com</a>
</address>`;

  const code5 = `<!-- Citing creative works -->
<p><cite>The Scream</cite> by Edvard Munch. Painted in 1893.</p>
<p><cite>Harry Potter and the Philosopher's Stone</cite> by J.K. Rowling.</p>
<p><cite>Star Wars: A New Hope</cite> directed by George Lucas.</p>

<!-- Citing references in academic work -->
<p>
  According to <cite>Research Methods in Education</cite> by Cohen et al., 
  the study followed rigorous methodology.
</p>

<!-- Multiple citations -->
<p>
  Popular programming books include 
  <cite>Clean Code</cite> by Robert Martin and 
  <cite>The Pragmatic Programmer</cite> by Hunt and Thomas.
</p>

<!-- Citation with link -->
<p>
  Read more in <cite><a href="https://example.com/book">Web Development Basics</a></cite>.
</p>`;

  const code6 = `<!-- Right-to-left text override -->
<p><bdo dir="rtl">This text will be written from right to left.</bdo></p>

<!-- Left-to-right override (useful for mixed content) -->
<p>English: <bdo dir="ltr">مرحبا بالعالم</bdo> (Hello World in Arabic)</p>

<!-- Practical use cases -->
<p>
  Normal: CodeFlare Limited<br>
  RTL: <bdo dir="rtl">CodeFlare Limited</bdo>
</p>

<!-- Mixed direction content -->
<p>
  English text <bdo dir="rtl">نص عربي</bdo> continues in English.
</p>

<!-- For mathematical expressions -->
<p>
  Equation: <bdo dir="rtl">x = y + z</bdo> (rendered right-to-left)
</p>`;

  const code7 = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Academic Paper Example</title>
  <style>
    body {
      font-family: 'Georgia', serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
      background: #f8f9fa;
    }
    
    .paper {
      background: white;
      padding: 3rem;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    
    .blockquote-custom {
      border-left: 4px solid #3b82f6;
      padding-left: 1.5rem;
      margin: 1.5rem 0;
      font-style: italic;
      color: #4b5563;
    }
    
    .blockquote-custom footer {
      margin-top: 0.5rem;
      font-style: normal;
      color: #6b7280;
      font-size: 0.9rem;
    }
    
    address {
      background: #f3f4f6;
      padding: 1rem;
      border-radius: 4px;
      font-style: normal;
      border-left: 3px solid #10b981;
    }
    
    abbr {
      text-decoration: none;
      border-bottom: 1px dotted #6b7280;
      cursor: help;
    }
    
    cite {
      color: #7c3aed;
      font-style: italic;
    }
    
    .bdo-demo {
      background: #fef3c7;
      padding: 0.5rem;
      border-radius: 4px;
      direction: rtl;
      text-align: right;
    }
  </style>
</head>
<body>
  <article class="paper">
    <header>
      <h1>The Impact of Technology on Modern Education</h1>
      <address>
        Written by <a href="mailto:author@university.edu">Dr. Sarah Johnson</a><br>
        Department of Computer Science<br>
        <abbr title="Massachusetts Institute of Technology">MIT</abbr><br>
        Cambridge, MA 02139
      </address>
    </header>
    
    <section>
      <h2>Introduction</h2>
      <p>
        As <cite>Digital Learning Revolution</cite> by Thompson states:
      </p>
      
      <blockquote class="blockquote-custom" cite="https://example.com/digital-learning">
        <p>
          The integration of technology in education has fundamentally transformed 
          traditional learning paradigms, creating new opportunities for personalized 
          learning and global collaboration. Educational institutions that embrace 
          these changes are better positioned to prepare students for the 
          <abbr title="Fourth Industrial Revolution">4IR</abbr>.
        </p>
        <footer>
          — Dr. Michael Thompson, <cite>Digital Learning Revolution</cite>, 2022
        </footer>
      </blockquote>
      
      <p>
        This sentiment is echoed by numerous educators. As one professor noted:
        <q>Technology is not just a tool; it's a gateway to new possibilities in education.</q>
      </p>
    </section>
    
    <section>
      <h2>Methodology</h2>
      <p>
        Our research followed the guidelines outlined in 
        <cite>Research Methods in Digital Education</cite> by Chen and Rodriguez.
        The study involved multiple institutions including 
        <abbr title="Stanford University">SU</abbr> and 
        <abbr title="Harvard University">HU</abbr>.
      </p>
    </section>
    
    <section>
      <h2>International Considerations</h2>
      <p>
        For our international colleagues, we provide this text in both directions:
      </p>
      <p>
        Normal: <q>Education knows no boundaries</q><br>
        Right-to-left: <span class="bdo-demo">Education knows no boundaries</span>
      </p>
      <p>
        Arabic text example: <bdo dir="rtl">التعليم لا يعرف حدودا</bdo>
      </p>
    </section>
    
    <footer>
      <address>
        Correspondence regarding this paper should be addressed to:<br>
        Research Department<br>
        Email: <a href="mailto:research@mit.edu">research@mit.edu</a><br>
        Phone: <a href="tel:+15551234567">+1 (555) 123-4567</a>
      </address>
      
      <blockquote class="blockquote-custom">
        <p>
          The future of education lies in the seamless integration of technology 
          and pedagogy, creating learning experiences that are both engaging and effective.
        </p>
        <footer>— Conference on Educational Technology, 2023</footer>
      </blockquote>
    </footer>
  </article>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Quotation Elements Introduction",
      content: "Specialized HTML elements for marking up quotations, citations, and references with semantic meaning",
      keyPoints: [
        "<q> for short inline quotations",
        "<blockquote> for longer block quotations",
        "Automatic styling by browsers",
        "Semantic meaning for accessibility and SEO"
      ],
      icon: <FaQuoteRight className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Block Quotations Deep Dive", 
      content: "Use blockquote for extended quotations from external sources with proper citation and formatting",
      keyPoints: [
        "Indented by default in browsers",
        "Use cite attribute for source URL",
        "Can include nested elements",
        "Perfect for academic and article content"
      ],
      icon: <FaQuoteLeft className="section-icon" />,
      color: "#059669",
      code: code2
    },
    {
      title: "Abbreviations and Acronyms",
      content: "Mark up abbreviations and acronyms to provide full expansions for better understanding and accessibility",
      keyPoints: [
        "Use <abbr> with title attribute",
        "Helpful for screen readers and translation",
        "Common in technical and academic writing",
        "Browsers often show tooltip on hover"
      ],
      icon: <FaGlobe className="section-icon" />,
      color: "#dc2626",
      code: code3
    },
    {
      title: "Contact Information with Address",
      content: "Use the address element to mark up contact information for documents, articles, or website authors",
      keyPoints: [
        "Typically renders in italic",
        "Can contain links and other elements",
        "Automatic line breaks in most browsers",
        "Ideal for article bylines and footers"
      ],
      icon: <FaMapMarkerAlt className="section-icon" />,
      color: "#7c3aed",
      code: code4
    },
    {
      title: "Citing Creative Works",
      content: "Use the cite element to reference titles of creative works like books, movies, paintings, and research papers",
      keyPoints: [
        "Typically renders in italic",
        "References work titles, not authors",
        "Can be used with or without links",
        "Important for academic and reference content"
      ],
      icon: <FaPenAlt className="section-icon" />,
      color: "#d97706",
      code: code5
    },
    {
      title: "Bidirectional Text Override",
      content: "Use bdo element to override the text direction for languages that read right-to-left or mixed content",
      keyPoints: [
        "dir='rtl' for right-to-left",
        "dir='ltr' for left-to-right override",
        "Essential for multilingual content",
        "Used with Arabic, Hebrew, and other RTL languages"
      ],
      icon: <FaLanguage className="section-icon" />,
      color: "#0891b2",
      code: code6
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 15 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Quotations & Citations</h1>
              <p className="html-lesson-subtitle">
                Master semantic markup for quotations, citations, abbreviations, and contact information
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Quotation Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Quotation Elements Power
              </div>
              <h3 className="html-visual-title">
                Semantic Markup for Professional Content and Citations
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Quotation Syntax</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<!-- Inline quote -->
<q>Short quotation</q>

<!-- Block quote -->
<blockquote>
  Extended quotation with citation
</blockquote>

<!-- Abbreviation -->
<abbr title="Full Form">ABBR</abbr>

<!-- Contact information -->
<address>Contact details</address>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Live Examples</h4>
                <div className="html-quote-preview">
                  <div className="quote-demo-item">
                    <p>Inline: <q>This is a short quote</q></p>
                  </div>
                  <div className="quote-demo-item">
                    <p>Abbreviation: <abbr title="HyperText Markup Language">HTML</abbr></p>
                  </div>
                  <div className="quote-demo-item">
                    <p>Citation: <cite>The Great Gatsby</cite></p>
                  </div>
                  <div className="quote-demo-item">
                    <p>BDO: <bdo dir="rtl">Right to Left</bdo></p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Semantic elements improve accessibility, SEO, and maintainability of your content.</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Quotation Elements Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaQuoteRight className="html-badge-icon" />
                Quotation Elements Guide
              </div>
              <h3 className="html-comparison-title">
                When to Use Each Quotation Element
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Element</th>
                    <th>Purpose</th>
                    <th>Default Styling</th>
                    <th>Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>&lt;q&gt;</code></td>
                    <td>Short inline quotations</td>
                    <td>Quotation marks</td>
                    <td>Brief quotes within sentences</td>
                  </tr>
                  <tr>
                    <td><code>&lt;blockquote&gt;</code></td>
                    <td>Extended block quotations</td>
                    <td>Indented with margins</td>
                    <td>Long quotes, citations, excerpts</td>
                  </tr>
                  <tr>
                    <td><code>&lt;abbr&gt;</code></td>
                    <td>Abbreviations and acronyms</td>
                    <td>Dotted underline (some browsers)</td>
                    <td>Technical terms, organizations</td>
                  </tr>
                  <tr>
                    <td><code>&lt;address&gt;</code></td>
                    <td>Contact information</td>
                    <td>Italic, line breaks</td>
                    <td>Author info, company contacts</td>
                  </tr>
                  <tr>
                    <td><code>&lt;cite&gt;</code></td>
                    <td>Work titles</td>
                    <td>Italic</td>
                    <td>Books, movies, research papers</td>
                  </tr>
                  <tr>
                    <td><code>&lt;bdo&gt;</code></td>
                    <td>Text direction override</td>
                    <td>Direction changes</td>
                    <td>Mixed language content, RTL text</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Quotation Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional Implementation Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use <code>&lt;q&gt;</code> for inline quotes within text</li>
                  <li>Include <code>cite</code> attribute for source URLs</li>
                  <li>Provide meaningful <code>title</code> for abbreviations</li>
                  <li>Use <code>&lt;address&gt;</code> for relevant contact info</li>
                  <li>Maintain proper nesting and structure</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using quotes for styling instead of semantics</li>
                  <li>Missing source citations for blockquotes</li>
                  <li>Using abbreviations without explanation</li>
                  <li>Overusing address for non-contact information</li>
                  <li>Ignoring accessibility with proper markup</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Semantic Benefits */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaGlobe className="html-badge-icon" />
                Semantic Benefits
              </div>
              <h3 className="html-multiple-title">
                Why Semantic Quotation Markup Matters
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>Key Benefits:</h4>
                <ul>
                  <li><strong>Accessibility:</strong> Screen readers can identify and properly announce quotes and abbreviations</li>
                  <li><strong>SEO:</strong> Search engines understand the context and importance of quoted content</li>
                  <li><strong>Maintainability:</strong> Clear semantic structure makes code easier to understand and update</li>
                  <li><strong>Internationalization:</strong> Proper markup supports translation and localization</li>
                  <li><strong>Future-proofing:</strong> Semantic HTML works across all browsers and devices</li>
                </ul>
              </div>
              
              <div className="html-semantic-examples">
                <h4>Real-world Impact:</h4>
                <div className="semantic-benefits">
                  <div className="benefit-item">
                    <div className="benefit-icon">♿</div>
                    <span>Better screen reader experience</span>
                  </div>
                  <div className="benefit-item">
                    <div className="benefit-icon">🔍</div>
                    <span>Improved search engine ranking</span>
                  </div>
                  <div className="benefit-item">
                    <div className="benefit-icon">🌍</div>
                    <span>Enhanced multilingual support</span>
                  </div>
                  <div className="benefit-item">
                    <div className="benefit-icon">⚡</div>
                    <span>Faster content processing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Academic Paper with Semantic Quotation Markup
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code7}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Quotation Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML quotations?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive quotation and citation elements including semantic markup for quotes, abbreviations, contact information, and bidirectional text. Start creating accessible, well-structured content!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-tables" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson15;